import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { MarkdownBlocks, parseMarkdown, type MdBlock } from "@/lib/miniMarkdown";
import privacyMd from "@/content/personalLegal/privacy-policy.md?raw";
import termsMd from "@/content/personalLegal/terms.md?raw";
import PersonalShell, { usePersonalProse } from "./PersonalShell";
import { FAMILY_FACE_DATA_POLICY_PATH } from "@/content/faceDataPolicy";

// The Photohawk Family app's privacy policy and terms (photohawk-personal legal/*.md, copied in by
// scripts/sync-personal-legal.sh): official, v1.0 in force from 20 October 2026, and public in production at
// /personal/privacy and /personal/terms (the App Store listing and the app link here), even where the rest of the new
// site is off (src/lib/homeFlag.ts). The face data policy is public at /face-data-policy/family
// (src/pages/FamilyFaceDataPolicy.tsx).

export type PersonalLegalDoc = "privacy" | "terms";

const SOURCES: Record<PersonalLegalDoc, string> = {
  privacy: privacyMd,
  terms: termsMd,
};

/** The texts' own links to photohawk.com/personal/… → this site's routes (the face data policy → its public page). */
export const personalRoute = (href: string): string | null => {
  const m = /^https:\/\/photohawk\.com\/personal\/([\w-]+)\/?$/.exec(href);
  if (!m) return null;
  return m[1] === "face-data-policy" || m[1] === "face-data" ? FAMILY_FACE_DATA_POLICY_PATH : `/personal/${m[1]}`;
};

/** Lines added since a text was published carry "[Pending legal review…]" in photohawk-personal until the owner
 *  approves them; they aren't part of the published version, so the website leaves them out. */
const PENDING = /\[Pending legal review/i;

/** The published part of a text: everything before a "(remove before publishing)" section (notes for counsel),
 *  without the paragraphs, list items and table rows still pending legal review. */
export const publishedBlocks = (md: string): MdBlock[] => {
  const blocks = parseMarkdown(md);
  const cut = blocks.findIndex((b) => b.type === "heading" && /remove before publishing/i.test(b.text));
  const kept = (cut < 0 ? blocks : blocks.slice(0, cut)).flatMap((b): MdBlock[] => {
    if ((b.type === "paragraph" || b.type === "quote") && PENDING.test(b.text)) return [];
    if (b.type === "list") {
      const items = b.items.filter((it) => !PENDING.test(it));
      return items.length ? [{ ...b, items }] : [];
    }
    if (b.type === "table") return [{ ...b, rows: b.rows.filter((r) => !r.some((c) => PENDING.test(c))) }];
    return [b];
  });
  while (kept.length && kept[kept.length - 1].type === "rule") kept.pop();
  return kept;
};

const link = (href: string, children: ReactNode, key: string) => {
  const route = personalRoute(href);
  return route ? (
    <Link key={key} to={route}>
      {children}
    </Link>
  ) : (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const PersonalLegalPage = ({ doc }: { doc: PersonalLegalDoc }) => {
  const blocks = useMemo(() => publishedBlocks(SOURCES[doc]), [doc]);
  const h1 = blocks.find((b) => b.type === "heading" && b.level === 1);
  const title = h1 && h1.type === "heading" ? h1.text : "Photohawk Family";
  const prose = usePersonalProse();
  return (
    <PersonalShell title={`${title} | Photohawk`}>
      <article className={prose} lang="en" data-testid="personal-legal">
        <MarkdownBlocks blocks={blocks} link={link} />
      </article>
    </PersonalShell>
  );
};

export default PersonalLegalPage;
